import {
  IonAvatar,
  IonButton,
  IonCardContent,
  IonCardTitle,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonImg,
  IonItemDivider,
  IonLabel,
  IonLoading,
  IonRow,
  IonSegment,
  IonSegmentButton,
  IonText,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { personAddOutline, personRemove } from "ionicons/icons";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { withRouter } from "react-router";

import PersonalAlbums from "../components/PersonalAlbums";
import SongList from "../components/SongList";
import Spacer from "../components/Spacer";

import { useAlbums } from "../data/store/useAlbums";
import { useArtists } from "../data/store/useArtists";
import { useFavorites } from "../data/store/useFavorites";
import { useProfile } from "../data/store/useProfile";
import { useSongs } from "../data/store/useSongs";
import { useTitle } from "../data/store/useTitle";

import CompactSongList from "../components/CompactSongList";
import { CompactToggle } from "../components/CompactToggle";
import { APP_TITLE } from "../config/env";
import { app } from "../services/LoginService";
// import {
//   warningEmptyPersonalSongs,
//   warningEmptyUserSongs,
// } from "../util/constants";
import {
  externalLink,
  sanitize,
  useIdParam,
  useProgressiveImage,
} from "../util/sugar";
import "./Avatar.scss";

import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { useRegions } from "../data/store/useRegions";

const ArtistDetail: React.FC = (props) => {
  const id = useIdParam(props);
  const { fetchSongsForArtist, clearArtistSongs, status } = useSongs();

  // Backs continuous autoplay for "Play All": load this artist's next page and
  // report the newly-loaded ids, so playback keeps going through the catalog a
  // page at a time (no slow up-front load of the whole catalog).
  const loadMoreArtist = async (): Promise<string[]> => {
    const before = new Set(
      (useSongs.getState().artists[id] || []).map((r) => r.id)
    );
    await useSongs.getState().fetchSongsForArtist(id);
    return (useSongs.getState().artists[id] || [])
      .map((r) => r.id)
      .filter((songId) => !before.has(songId));
  };
  const { fetchMeta, shouldFetchMeta } = useArtists();
  const { all: allAlbums, fetchAll: fetchAllAlbums } = useAlbums();
  const { ownerId, authorId, compact } = useProfile();
  const artist = useArtists((x) => x.retrieve(id));
  // Set once fetchMeta's Firestore read rejects (permission-denied for a
  // Members-Only profile, or a genuinely missing id) — distinguishes
  // "still loading" from "not visible to you" so we don't render an empty
  // shell forever. See canReadArtist() in firestore.rules.
  const artistUnavailable = useArtists((x) => x.empty.includes(id));
  const songs = useSongs((x) => x.retrieveByArtist(id));
  const artistsSongsNumber = useSongs((x) => x.artistNumberSongs);
  const setTitle = useTitle((x) => x.setTitle);
  const { isFollowing, follow, unfollow } = useFavorites();

  const [songsList, setSongsList] = useState(songs);
  const [loadedArtistId, setLoadedArtistId] = useState("");
  const [avatarStatus, setAvatarStatus] = useState("hidden");
  const [artistState, setArtistState] = useState("songs");
  const bgRef = useRef<HTMLIonContentElement>(null);
  const following = isFollowing(id);
  const totalSongsNumber = artistsSongsNumber[id];
  const artistWall = artist && artist.wall;
  const alias = artist && artist.alias;
  const region = useRegions((x) =>
    artist?.region ? x.regions[artist?.region] : ""
  );

  const current = loadedArtistId === id;
  // const isLoading = artistStatus === "fetching" || status === "fetching";
  const userIsOwner = current && loadedArtistId !== authorId;
  // const warningSongText = userIsOwner
  //   ? warningEmptyUserSongs
  //   : warningEmptyPersonalSongs;
  const loaded = useProgressiveImage(artistWall);

  // Only show the Songs/Albums tabs when this artist actually has albums to show
  // (ones they made, or that were compiled about them). Owners also count their
  // own private albums; visitors only see public ones. Album creation is still
  // reachable from the Albums page in the menu, so hiding the tabs never strands
  // the owner.
  const isAlbumOwner = !!artist && artist.ownerId === ownerId;
  const hasAlbums = allAlbums.some(
    (a) =>
      ((!!artist && a.ownerId === artist.ownerId) || a.authorId === id) &&
      (isAlbumOwner || !a.isPrivate)
  );
  const effectiveArtistState = hasAlbums ? artistState : "songs";

  const toggleArtistState = ({ detail: { value } }: any) => {
    setArtistState(value);
  };

  useIonViewWillLeave(() => {
    setLoadedArtistId("");
  });

  useLayoutEffect(() => {
    shouldFetchMeta(id) && fetchMeta(id);
    // Start each artist's feed from a clean slate so it paginates from the
    // newest song (see clearArtistSongs) rather than jumping to the deep past.
    clearArtistSongs(id);
    fetchSongsForArtist(id);
    // Load albums so we know whether to show the Songs/Albums tabs.
    fetchAllAlbums();
  }, [id]);

  useEffect(() => {
    alias && setTitle(alias);
  }, [alias, setTitle]);

  useEffect(() => {
    artist && setLoadedArtistId(artist.id);
  }, [artist]);

  useEffect(() => {
    setSongsList(songs);
  }, [songs]);
  useIonViewWillEnter(() => {
    firebase.analytics(app).logEvent("view_artist", id);
    setLoadedArtistId(id);
    shouldFetchMeta(id) && fetchMeta(id);
  });
  return (
    <IonContent
      className={
        loaded ? "artist-wall sadfam-content" : "artist-bg sadfam-content"
      }
      style={{
        "--ion-background-color": "clear",
        background: `url(${loaded || ""})`,
      }}
      ref={bgRef}
    >
      <Spacer height={50} />
      <IonGrid>
        <IonLoading
          isOpen={status === "fetching" && (songsList || []).length === 0}
        />
        {artistUnavailable ? (
          <IonRow>
            <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
              <IonCardContent color="paper" className="centerColumn" style={{ borderRadius: "8px", margin: "0 16px" }}>
                <IonText color="dark">
                  <p className="ion-text-center">
                    This profile is private or doesn&apos;t exist.
                  </p>
                </IonText>
              </IonCardContent>
            </IonCol>
          </IonRow>
        ) : (
        <IonRow>
          <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
            <IonCardContent
              color="paper"
              style={{
                backdropFilter: "blur(10px)",
                borderRadius: "8px",
                margin: "0 16px",
              }}
              className="centerColumn"
            >
              {current && artist && (
                <IonAvatar
                  className={`${avatarStatus} avatarBorder`}
                  style={{
                    width: 108,
                    height: 108,
                  }}
                >
                  <IonImg
                    onIonImgDidLoad={() => setAvatarStatus("loaded")}
                    // Falls back to this instance's branded icon (swapped
                    // per-instance at build time, see scripts/stage-instance.js)
                    // so artists without an uploaded avatar still show
                    // something recognizable instead of a blank circle.
                    src={artist.avatar || "/assets/icon/android-chrome-512x512.png"}
                    alt={artist.avatar ? (alias ? `${alias} avatar` : "Artist avatar") : `${APP_TITLE} logo`}
                  />
                </IonAvatar>
              )}

              {current && artist && artist.statement && (
                <IonCardTitle className="ion-text-center">
                  <IonText
                    color="dark"
                    style={{
                      textShadow: "none",
                    }}
                  >
                    {artist.statement}
                  </IonText>
                </IonCardTitle>
              )}
              {region && region.id !== "XXX" && (
                <IonButton
                  expand="full"
                  fill="clear"
                  routerLink={"/region-artists/" + region.id}
                >
                  {region.city}
                </IonButton>
              )}

              {current && artist && artist.website && (
                <a
                  className="avatarLink"
                  rel="noopener noreferrer"
                  target="_blank"
                  href={externalLink(sanitize(artist.website))}
                >
                  <IonText color="tertiary">{artist.website}</IonText>
                </a>
              )}

              <IonItemDivider color="clear" />
              {userIsOwner && (
                <IonButton
                  fill="outline"
                  onClick={() =>
                    following ? unfollow(ownerId, id) : follow(ownerId, id)
                  }
                >
                  {following ? (
                    <IonIcon slot="start" color="primary" icon={personRemove} aria-hidden="true" />
                  ) : (
                    <IonIcon slot="start" icon={personAddOutline} aria-hidden="true" />
                  )}
                  <IonLabel>
                    {following ? "Unfollow" : "Follow"} {alias}
                  </IonLabel>
                </IonButton>
              )}

              {hasAlbums && (
                <IonSegment
                  color="medium"
                  value={artistState}
                  onIonChange={toggleArtistState}
                >
                  <IonSegmentButton value="songs">Songs</IonSegmentButton>
                  <IonSegmentButton value="albums">Albums</IonSegmentButton>
                </IonSegment>
              )}

              {/* Sits immediately above the song list so it reads as "view
                  mode for what's below" rather than a profile-info field
                  mixed in with the avatar/bio/follow controls above. */}
              <CompactToggle edgeInset={20} />
            </IonCardContent>

            {effectiveArtistState === "songs" && current && songsList ? (
              <>
                {!compact ? (
                  <SongList
                    status={status}
                    songRefs={songsList}
                    hideFetchButton={songsList.length === totalSongsNumber}
                    fetchMore={() => fetchSongsForArtist(id)}
                    loadMore={loadMoreArtist}
                  />
                ) : (
                  <CompactSongList
                    status={status}
                    songRefs={songsList}
                    hideFetchButton={songsList.length === totalSongsNumber}
                    fetchMore={() => {
                      fetchSongsForArtist(id);
                    }}
                    loadMore={loadMoreArtist}
                  />
                )}
              </>
            ) : (
              <>
                <Spacer height={30} />
                {status !== "fetching" && (
                  <PersonalAlbums id={(artist && artist.ownerId) || ""} artistId={id} />
                )}
              </>
            )}
          </IonCol>
        </IonRow>
        )}
      </IonGrid>
    </IonContent>
  );
};

export default withRouter(ArtistDetail as any);
